"use client";

import { motion, useReducedMotion } from "framer-motion";
import { StarIcon } from "@heroicons/react/solid";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { LogoRow } from "./logo-row";
import { UseCaseTabs } from "./use-case-tabs";

const EASE = [0.16, 1, 0.3, 1] as const;

const INTEGRATION_ICONS = [
  { src: "/images/integrations/mindbody/icon.png", alt: "Mindbody" },
  { src: "/images/integrations/fitdegree/icon.png", alt: "Fitdegree" },
  { src: "/images/integrations/mariana-tek/icon.png", alt: "Mariana Tek" },
  { src: "/images/integrations/facebook/icon.png", alt: "Facebook" },
  { src: "/images/integrations/instagram/icon.png", alt: "Instagram" },
];

export function Hero() {
  const reduceMotion = useReducedMotion();

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };

  const MotionDiv = reduceMotion ? "div" : motion.div;
  const motionProps = reduceMotion
    ? {}
    : { initial: "hidden", animate: "visible", variants: container };

  return (
    // z-10 + opaque background so this section acts as a curtain that scrolls
    // up to reveal the pinned pull-quote sitting behind it (z-0).
    <section className="relative z-10 overflow-x-clip bg-background pt-28 lg:pt-36 border-b border-border max-sm:pb-5 sm:pb-14 lg:pb-[4.5rem]">
      {/* Ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[680px]" />

      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <MotionDiv className="mx-auto flex max-w-5xl flex-col text-center" {...motionProps}>
          {/* Eyebrow — on mobile it drops below the subhead so the H1 leads. */}
          <MotionDiv
            variants={reduceMotion ? undefined : item}
            className="order-3 flex justify-center max-sm:mt-7 sm:order-none"
          >
            <div className="inline-flex max-w-full flex-col items-center gap-3 sm:flex-row sm:gap-2.5 sm:rounded-full sm:border sm:border-border sm:bg-background/80 sm:py-1 sm:pl-1.5 sm:pr-3.5 sm:shadow-sm sm:backdrop-blur">
              <span className="flex shrink-0 -space-x-1.5 sm:-space-x-1">
                {INTEGRATION_ICONS.map((icon) => (
                  <Image
                    key={icon.alt}
                    src={icon.src}
                    alt={icon.alt}
                    width={36}
                    height={36}
                    className="h-6 w-6 rounded-full bg-background ring-2 ring-background dark:ring-white/15 sm:h-5 sm:w-5"
                  />
                ))}
              </span>
              <span className="text-balance text-center text-[13px] font-medium text-muted-foreground sm:text-left sm:text-xs">
                Leverage the data you already have to unlock your growth potential
              </span>
            </div>
          </MotionDiv>

          <motion.h1
            variants={reduceMotion ? undefined : item}
            className="order-1 mt-6 text-[2.5rem] font-bold leading-[1.05] tracking-tight text-foreground max-sm:mt-0 sm:order-none sm:text-5xl lg:text-[3.5rem]"
          >
            The growth engine for fitness brands
          </motion.h1>

          <motion.p
            variants={reduceMotion ? undefined : item}
            className="order-2 mx-auto mt-6 max-w-2xl text-balance leading-relaxed text-muted-foreground sm:order-none sm:text-lg"
          >
            Helios powers authentic engagement at scale for growing fitness brands.
            <span className="block sm:hidden"> </span>
            <br className="hidden sm:block" />
            Ensure every lead is nurtured, every member is engaged, and every location executes the
            playbook for growth.
          </motion.p>

          <motion.div
            variants={reduceMotion ? undefined : item}
            className="order-4 mt-8 flex flex-col items-center justify-center gap-3 sm:order-none sm:flex-row sm:gap-4"
          >
            <Button asChild size="xl" className="group w-full sm:w-auto">
              <Link href="/book-a-demo">
                Book a demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </motion.div>

          {/* Social proof */}
          <motion.div
            variants={reduceMotion ? undefined : item}
            className="order-5 mt-10 flex flex-col items-center gap-2.5 sm:order-none"
          >
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <StarIcon key={i} className="h-4 w-4 text-amber-400" />
              ))}
            </div>
            <p className="text-xs font-medium text-foreground">
              Loved by leading fitness studios and franchises
            </p>
            <LogoRow className="justify-center" />
          </motion.div>
        </MotionDiv>

        {/* Use-case tabs + focused product views */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 40 }}
          animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="mx-auto mt-12 max-w-6xl lg:mt-16"
        >
          <UseCaseTabs />
        </motion.div>
      </div>
    </section>
  );
}
